import React from 'react';
import {
  Box,
  Modal,
  Card,
  CardContent,
  Button,
  Portal,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import { useSaleCalendar } from '../SaleCalendarContext';
import { eachDayOfInterval } from 'date-fns';
import { useSelector } from 'react-redux';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const DAYS = [
  { label: 'Pn', value: 'monday' },
  { label: 'Wt', value: 'tuesday' },
  { label: 'Śr', value: 'wednesday' },
  { label: 'Cz', value: 'thursday' },
  { label: 'Pt', value: 'friday' },
  { label: 'Sb', value: 'saturday' },
  { label: 'Nd', value: 'sunday' }
];

const WEEKDAY_MAP = {
  saturday: 0,
  sunday: 1,
  monday: 2,
  tuesday: 3,
  wednesday: 4,
  thursday: 5,
  friday: 6
};

const SHARED_CAPACITY_GROUPS = [
  [
    '3 DNI - MUNDUROWA',
    '3 DNI GIEWARTÓW',
    '3 DNI MRÓWKI',
    '3 DNI',
    'WEEKEND',
    'WEEKEND GIEWARTÓW',
    'WEEKEND MRÓWKI',
    '5 DNI - MUNDUROWA',
    '5 DNI GIEWARTÓW',
    '5 DNI MRÓWKI',
    '5 DNI'
  ]
];
const SHARED_CAPACITY_GROUPS_1_DAY = [['1 DZIEŃ', '1 DZIEŃ - MUNDUROWA']];
const ALL_SHARED_CAPACITY_GROUPS = [
  ...SHARED_CAPACITY_GROUPS,
  ...SHARED_CAPACITY_GROUPS_1_DAY
];

export default function SlotsControlModal({ open, handleClose, targetDate }) {
  const { setSlotControlByDay, submitSaleCalendar, periods } = useSaleCalendar();
  const { seasonYear } = useSelector(state => state.app);
  const [days, setDays] = React.useState('all');
  const [capacity, setCapacity] = React.useState('');
  const [selectedPeriods, setSelectedPeriods] = React.useState(periods.map(p => p.id));

  const handleChange = e => setCapacity(e.target.value);

  const handleDayToggle = (event, newDays) => {
    if (newDays.length === 0) {
      setDays([]);
    } else {
      setDays(newDays);
    }
  };

  const groupMap = React.useMemo(() => {
    const map = {};
    ALL_SHARED_CAPACITY_GROUPS.forEach((group, index) => {
      const groupId = `shared-group-${index}`;
      periods.forEach(period => {
        if (group.includes(period.title)) {
          map[period.id] = groupId;
        }
      });
    });

    return map;
  }, [periods]);

  const togglePeriod = id => {
    const groupId = groupMap[id];
    const idsToToggle = groupId
      ? Object.keys(groupMap).filter(pid => groupMap[pid] === groupId)
        .map(Number)
      : [id];

    const allSelected = idsToToggle.every(pid => selectedPeriods.includes(pid));

    setSelectedPeriods(prev => allSelected
      ? prev.filter(pid => !idsToToggle.includes(pid))
      : [...new Set([...prev, ...idsToToggle])]
    );
  };

  const handleSelectAllPeriods = () => {
    if (selectedPeriods.length === periods.length) {
      setSelectedPeriods([]);
    } else {
      setSelectedPeriods(periods.map(p => p.id));
    }
  };

  const handleSubmit = () => {
    const selectedDays = days === 'all' ? Object.keys(WEEKDAY_MAP) : days;

    let allDays = [];

    if (targetDate) {
      const day = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
      allDays = [day];
    } else {
      const start = new Date(seasonYear, 0, 1);
      const end = new Date(seasonYear, 11, 31);
      allDays = eachDayOfInterval({ start, end });
    }

    const matchingDays = allDays.filter(date => selectedDays.includes(
      Object.keys(WEEKDAY_MAP).find(key => WEEKDAY_MAP[key] === date.getDay())
    )
    );

    const newItems = [];

    for (const day of matchingDays) {
      const dayStr = day.toISOString().slice(0, 10);

      const groupMapByGroupId = {};
      for (const pid of selectedPeriods) {
        const groupId = groupMap[pid] || `single-${pid}`;
        if (!groupMapByGroupId[groupId]) {
          groupMapByGroupId[groupId] = [];
        }
        groupMapByGroupId[groupId].push(pid);
      }

      for (const groupId in groupMapByGroupId) {
        const groupPeriods = groupMapByGroupId[groupId];
        const representativePid = Math.min(...groupPeriods.map(Number));
        newItems.push({
          day: dayStr,
          capacity,
          period: representativePid
        });
      }
    }

    setSlotControlByDay(prev => {
      const updated = [...prev];

      for (const newItem of newItems) {
        const existingIndex = updated.findIndex(
          item => item.day === newItem.day && item.period === newItem.period
        );

        if (existingIndex !== -1) {
          updated[existingIndex] = newItem;
        } else {
          updated.push(newItem);
        }
      }

      submitSaleCalendar(updated);

      return updated;
    });

    handleClose();
  };

  return (
    <Portal>
      <Modal open={open} onClose={handleClose} id="block-modal">
        <Card sx={{ ...style, width: '550px' }}>
          <CardContent>
            <Typography variant="body1" textAlign="center">
              {targetDate
                ? `Ustaw ilość miejsc dla dnia ${new Date(targetDate).toLocaleDateString()}`
                : 'Ustaw ilość miejsc w dniach z całego roku'}
            </Typography>

            <Box height={20} />
            <Typography variant="body2" textAlign="center" color="red">
              Uwaga! Po kliknięciu przycisku zapisz nadpisze obecną konfigurację nowymi danymi
            </Typography>

            <Box height={20} />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle2" flex="1 1 30%">Ilość miejsc</Typography>
              <TextField
                label="ilość miejsc..."
                fullWidth
                size="small"
                value={capacity}
                onChange={handleChange}
              />
            </Stack>

            {!targetDate && (
              <Box mt={3}>
                <Typography variant="subtitle2">Dni tygodnia</Typography>
                <ToggleButtonGroup
                  value={days === 'all' ? DAYS.map(d => d.value) : days}
                  onChange={handleDayToggle}
                  size="small"
                  fullWidth
                  color="primary"
                >
                  {DAYS.map(day => (
                    <ToggleButton key={day.value} value={day.value}>
                      {day.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Button
                  size="small"
                  onClick={() => setDays(
                    days === 'all' || days.length === DAYS.length
                      ? []
                      : DAYS.map(d => d.value)
                  )
                  }
                >
                  {days.length === DAYS.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2">Wybierz okresy</Typography>
              <FormGroup>
                {periods.map(period => (
                  <FormControlLabel
                    key={period.id}
                    control={
                      <Checkbox
                        checked={selectedPeriods.includes(period.id)}
                        onChange={() => togglePeriod(period.id)}
                      />
                    }
                    label={period.title}
                  />
                ))}
              </FormGroup>
              <Button size="small" onClick={handleSelectAllPeriods}>
                {selectedPeriods.length === periods.length ? 'Odznacz wszystkie' : 'Zaznacz wszystkie'}
              </Button>
            </Box>

            <Box height={20} />
            <Button variant="contained" fullWidth onClick={handleSubmit}>
              Zapisz
            </Button>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
  );
}
